'use client';

import { StepperStepLabelSlot, StepperStepSlot } from '@magicschool/spellbook/components/Stepper/slots';
import { Text } from '@magicschool/spellbook/components/Text';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { cloneElement, Fragment } from 'react';

export type StepperProps = React.ComponentProps<'nav'> &
  VariantProps<typeof stepperVariants> & {
    /** The title of the stepper, displayed above the step, or as an `aria-label` if hideTitle is `true`. */
    title: string;
    /** When `true` (default), hides the visible title and applies it as `aria-label` on the `<nav>` element. */
    hideTitle?: boolean;
    /** The index (0-based) of the current step. */
    currentStepIndex?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
  };

export type StepperStepProps = React.ComponentProps<'li'> & {
  isCurrent?: boolean;
  isCompleted?: boolean;
  isLast?: boolean;
};

const stepperVariants = cva('flex flex-col relative items-center gap-4', {
  variants: {
    /** Whether the stepper should take up the full width of its container. */
    fullWidth: {
      true: 'w-full',
      false: 'w-fit',
    },
  },
});

const StepperBase: React.FC<StepperProps> = ({
  currentStepIndex = 0,
  children,
  className,
  title,
  fullWidth,
  hideTitle = true,
  ...props
}) => {
  const { orderedSlots } = useOrderedSlots(children, {
    step: { Component: StepperStepSlot },
  });

  if (orderedSlots.length === 0) {
    console.warn('Stepper requires at least one `Stepper.Step` child component.');
  }

  if (currentStepIndex < 0 || currentStepIndex >= orderedSlots.length) {
    console.warn(`Stepper \`currentStepIndex\` must be between 0 and ${orderedSlots.length - 1}.`);
  }

  return (
    <nav
      data-spellbook-name="stepper"
      aria-label={hideTitle ? title : undefined}
      className={cn(stepperVariants({ fullWidth }), className)}
      {...props}
    >
      {!hideTitle && <Text variant="heading-3">{title}</Text>}
      <div className="relative w-full">
        <ol className="flex items-start justify-between w-full">
          {orderedSlots.map((slot, index) => {
            const isCurrent = index === currentStepIndex;
            const isCompleted = index < currentStepIndex;
            const isLast = index === orderedSlots.length - 1;
            const Component = slot.component as React.ReactElement<StepperStepProps>;

            return (
              <Fragment key={`step-${index}`}>
                {cloneElement(Component, {
                  ...(Component.props ?? {}),
                  isCurrent,
                  isCompleted,
                  isLast,
                })}
              </Fragment>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

/**
 * Display-only progress indicator that shows a series of steps in a linear flow. Controlled via `currentStepIndex` to reflect the user's position in a multi-step process.
 *
 * ### When to use
 * - To visualize progress through a multi-step workflow such as a form wizard, onboarding flow, or setup process
 * - When users need to see how many steps remain and which step they are on
 *
 * ### When NOT to use
 * - For switchable content panels where users freely jump between sections: use `Tabs` instead
 * - For navigation between pages: use `NavLink` or route-based navigation
 * - For a single progress bar without discrete steps: use a progress bar or loading indicator
 *
 * ### API
 * - `title`: Descriptive label for the stepper. Displayed above the steps, or used as `aria-label` when `hideTitle` is `true`
 * - `hideTitle`: When `true` (default), hides the visible title and applies it as `aria-label` on the `<nav>` element
 * - `currentStepIndex`: 0-based index (0–7) of the active step. Steps before this index appear as completed, steps after appear as upcoming
 * - `fullWidth`: When `true`, the stepper expands to fill its parent container width
 *
 * ### Composition (slots)
 * - `<Stepper.Step>`: Required. Represents a single step. Renders as an `<li>` with a step icon, connecting divider, and label
 * - `<Stepper.StepLabel>`: Optional override for the default label rendering. Rendered by default using the `Step` children text. Use to customize label styling
 *
 * ### Visual states
 * - **Completed** (index < `currentStepIndex`): Filled check-circle icon in primary color, primary-colored connecting divider
 * - **Current** (index === `currentStepIndex`): Circle icon in primary color, bold label text (`label-2`, `default-strong`)
 * - **Upcoming** (index > `currentStepIndex`): Circle icon in default color, lighter label text (`label-1`, `default-soft`)
 *
 * ### Accessibility
 * - Renders as a `<nav>` containing an `<ol>` for semantic step ordering
 * - The current step receives `aria-current="step"` so screen readers announce it
 * - When `hideTitle` is `true`, the `title` is applied as `aria-label` on the `<nav>` element
 * - Connecting dividers between steps are marked `decorative` to avoid screen reader noise
 */
export const Stepper = Object.assign(StepperBase, {
  Step: StepperStepSlot,
  StepLabel: StepperStepLabelSlot,
});
